import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectProps } from '@perxis-auto/ui-kit/lib/components/Select';

import { FilterLocationProps } from './FilterLocation.types';

function FilterLocation({
  value = [],
  options = [],
  filterSort,
  onChange,
  ...props
}: FilterLocationProps) {
  const { t } = useTranslation();

  const clusters = useMemo(
    () => options.filter(option => option.type === 'cluster'),
    [options],
  );

  const cities = useMemo(() => {
    const items = options.filter(option => option.type !== 'cluster');
    if (filterSort) {
      return items.sort(filterSort);
    }
    return items;
  }, [options, filterSort]);

  const resolvedValue = useMemo(() => {
    if (!clusters.length) {
      return value;
    }

    const valueWithClusters = [...value];

    for (const clusterOption of clusters) {
      const cityOptions = options.filter(
        option => option.clusterId === clusterOption.value,
      );

      if (!cityOptions.length) {
        continue;
      }

      const isAllCitiesSelected = cityOptions.every(option =>
        value.includes(option.value),
      );

      if (isAllCitiesSelected) {
        valueWithClusters.unshift(clusterOption.value);
      }
    }

    return valueWithClusters;
  }, [value, options, clusters]);

  const handleLocationChange = useCallback(
    (selectedValues?: string[], selectedOptions?: any[]) => {
      if (!onChange) {
        return;
      }

      if (!selectedValues || !selectedValues.length) {
        return onChange(selectedValues);
      }

      const removedClusters = resolvedValue.filter(v => !selectedValues.includes(v));

      const newValues = selectedValues.reduce<string[]>((acc, value, index) => {
        const selectedOption = selectedOptions?.[index];

        if (!selectedOption || removedClusters.includes(selectedOption.clusterId)) {
          return acc;
        }

        if (selectedOption.type !== 'cluster') {
          return [...acc, value];
        }

        if (resolvedValue.includes(value)) {
          return acc;
        }

        const cityOptions = options.filter(option => option.clusterId === value);

        return Array.from(new Set([...acc, ...cityOptions.map(option => option.value)]));
      }, []);

      return onChange(newValues);
    },
    [onChange, options, resolvedValue],
  );

  return (
    <Select
      mode="multiple"
      label={t('defaultFilters.location.city.label')}
      onChange={handleLocationChange as SelectProps['onClear']}
      options={[...clusters, ...cities]}
      value={resolvedValue}
      maxTagCount={1000}
      maxTagTextLength={12}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      showSearch
      {...props}
    />
  );
}

export default FilterLocation;
